import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

/**
 * One body, two chromes.
 *
 * Design, Physical layout and the single-line diagram each grew up as modals.
 * They now also have to render as panels inside the Electrical Design tab,
 * where a dialog is the wrong shape: the user is working through steps 1-2-3,
 * and a modal that has to be dismissed to see the next step hides the very
 * sequence the tab exists to show.
 *
 * Rather than fork ~2000 lines of dialog body into panel copies, these four
 * components swap only the OUTER elements. Everything inside is untouched, so
 * the two modes cannot drift apart -- a fix to the layout maths or the diagram
 * lands in both because there is only one of each.
 *
 * `inline` false is the original behaviour, exactly; nothing that opens these
 * as modals today needs to change.
 */
export function PanelShell({ inline, children, ...dialogProps }) {
  if (inline) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        {children}
      </Box>
    );
  }
  return (
    <Dialog fullWidth {...dialogProps}>
      {children}
    </Dialog>
  );
}

/**
 * Inline, the tab strip already says which step you are on, so repeating it as
 * a heading is noise -- but the unsaved marker is not, so the title still
 * renders when it carries one.
 */
export function PanelShellTitle({ inline, children, sx, ...rest }) {
  if (inline) {
    return (
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 800, mb: 1.25, color: "text.secondary", ...sx }}
      >
        {children}
      </Typography>
    );
  }
  return (
    <DialogTitle sx={sx} {...rest}>
      {children}
    </DialogTitle>
  );
}

/**
 * A dialog's content scrolls inside a fixed-height shell. A panel should not:
 * it sits in the page and the page scrolls, so the height constraints and the
 * `dividers` rules are dropped rather than carried over, which is what made the
 * first attempt render a scrollbar inside a scrollbar.
 */
export function PanelShellContent({ inline, children, sx, dividers, ...rest }) {
  if (inline) {
    const { overflow, ...rest_sx } = sx || {};
    return <Box sx={{ ...rest_sx, px: 0 }}>{children}</Box>;
  }
  return (
    <DialogContent dividers={dividers} sx={sx} {...rest}>
      {children}
    </DialogContent>
  );
}

export function PanelShellActions({ inline, children, sx, ...rest }) {
  if (inline) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          pt: 2,
          mt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          ...sx,
        }}
      >
        {children}
      </Box>
    );
  }
  return (
    <DialogActions sx={sx} {...rest}>
      {children}
    </DialogActions>
  );
}
